import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { projectsAPI, tasksAPI, templatesAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { useProject } from '../../layout/ProjectLayout';
import { Field, LabelChip, btnGhost, btnPrimary, inputCls } from '../../components/ui';

const SWATCHES = ['#c8a557', '#5b8dd9', '#6aa974', '#b56ac2', '#d98a5b', '#5bc2b8', '#a3a94f', '#c25b6a', '#9aa3b2'];

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-pm-faint">{title}</h2>
      <div className="rounded-lg border border-pm-border bg-pm-surface p-4">{children}</div>
    </section>
  );
}

export default function ProjectSettings() {
  const { project, roster, reload } = useProject();
  const navigate = useNavigate();
  const user = getUser();
  const readOnly = user?.role === 'guest';

  // details
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [color, setColor] = useState(project.color);
  const [savedFlash, setSavedFlash] = useState(false);

  // members
  const [memberIds, setMemberIds] = useState(project.member_ids || []);
  const [guestIds, setGuestIds] = useState(project.guest_ids || []);

  // groups + labels + custom fields
  const [groups, setGroups] = useState([]);
  const [labels, setLabels] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [fields, setFields] = useState([]);
  const [newField, setNewField] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');

  const loadLists = useCallback(async () => {
    const [g, l, f] = await Promise.all([
      tasksAPI.groups(project.id),
      tasksAPI.labels(project.id),
      tasksAPI.customFields(project.id),
    ]);
    setGroups(g.data.groups);
    setLabels(l.data.labels);
    setFields(f.data.fields);
  }, [project.id]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const saveDetails = async (e) => {
    e.preventDefault();
    await projectsAPI.update(project.id, { name, description, color });
    await reload();
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const cycleRole = async (mid) => {
    // none -> member -> guest -> none
    let m = [...memberIds];
    let g = [...guestIds];
    if (m.includes(mid)) {
      m = m.filter((x) => x !== mid);
      g = [...g, mid];
    } else if (g.includes(mid)) {
      g = g.filter((x) => x !== mid);
    } else {
      m = [...m, mid];
    }
    setMemberIds(m);
    setGuestIds(g);
    await projectsAPI.setMembers(project.id, { member_ids: m, guest_ids: g });
    await reload();
  };

  const roleOf = (mid) => (memberIds.includes(mid) ? 'member' : guestIds.includes(mid) ? 'guest' : 'none');

  const renameGroup = async (group, newName) => {
    if (!newName.trim() || newName === group.name) return;
    await tasksAPI.updateGroup(group.id, { name: newName.trim(), color: group.color, is_done_group: group.is_done_group });
    await loadLists();
  };

  const moveGroup = async (index, dir) => {
    const ids = groups.map((g) => g.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    await tasksAPI.reorderGroups(project.id, ids);
    await loadLists();
  };

  const deleteGroup = async (group) => {
    try {
      await tasksAPI.deleteGroup(group.id);
      await loadLists();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Cannot delete group');
    }
  };

  const addLabel = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    await tasksAPI.createLabel(project.id, { name: newLabel.trim(), color: SWATCHES[labels.length % SWATCHES.length] });
    setNewLabel('');
    await loadLists();
  };

  const deleteLabel = async (label) => {
    await tasksAPI.deleteLabel(label.id);
    await loadLists();
  };

  const deleteProject = async () => {
    const typed = window.prompt(`Type the project name to delete permanently:\n${project.name}`);
    if (typed !== project.name) return;
    await projectsAPI.remove(project.id);
    navigate('/');
  };

  if (readOnly) {
    return <p className="text-sm text-pm-muted">Guests cannot access project settings.</p>;
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Section title="Details">
        <form onSubmit={saveDetails} className="space-y-4">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} />
          </Field>
          <Field label="Description">
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inputCls} />
          </Field>
          <div>
            <span className="mb-1.5 block text-sm text-pm-muted">Color</span>
            <div className="flex gap-2">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-6 w-6 rounded-full ${color === c ? 'ring-2 ring-pm-text ring-offset-2 ring-offset-pm-surface' : ''}`}
                  style={{ background: c }}
                  title={c}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            {savedFlash && <span className="text-xs text-pm-accent">Saved</span>}
            <button type="submit" className={btnPrimary}>
              Save details
            </button>
          </div>
        </form>
      </Section>

      <Section title="People">
        <p className="mb-3 text-xs text-pm-faint">
          Click a person to cycle their access: none → member → guest (read + comment). Only members attached to the
          tool by the product admin appear here.
        </p>
        <ul className="divide-y divide-pm-border/60">
          {roster.map((m) => {
            const role = roleOf(m.mid);
            return (
              <li key={m.mid} className="flex items-center gap-3 py-2">
                <span className="text-sm">{m.name}</span>
                <span className="text-xs text-pm-faint">{m.email}</span>
                <button
                  type="button"
                  onClick={() => cycleRole(m.mid)}
                  className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    role === 'member'
                      ? 'bg-pm-accent text-pm-accent-ink'
                      : role === 'guest'
                        ? 'bg-pm-raised text-pm-text'
                        : 'bg-pm-raised text-pm-faint'
                  }`}
                >
                  {role === 'none' ? 'No access' : role}
                </button>
              </li>
            );
          })}
          {roster.length === 0 && <li className="py-2 text-xs text-pm-faint">No attached members yet.</li>}
        </ul>
      </Section>

      <Section title="Task groups">
        <ul className="divide-y divide-pm-border/60">
          {groups.map((g, i) => (
            <li key={g.id} className="flex items-center gap-2 py-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: g.color }} />
              <input
                defaultValue={g.name}
                onBlur={(e) => renameGroup(g, e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none focus:text-pm-accent"
              />
              {g.is_done_group && <span className="text-[10px] uppercase text-pm-faint">done group</span>}
              <button type="button" onClick={() => moveGroup(i, -1)} disabled={i === 0} className="rounded p-1 text-pm-faint hover:text-pm-text disabled:opacity-30">
                <ArrowUp size={13} />
              </button>
              <button type="button" onClick={() => moveGroup(i, 1)} disabled={i === groups.length - 1} className="rounded p-1 text-pm-faint hover:text-pm-text disabled:opacity-30">
                <ArrowDown size={13} />
              </button>
              <button type="button" onClick={() => deleteGroup(g)} className="rounded p-1 text-pm-faint hover:text-pm-danger" title="Delete (must be empty)">
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Labels">
        <div className="flex flex-wrap items-center gap-2">
          {labels.map((l) => (
            <span key={l.id} className="group inline-flex items-center gap-1">
              <LabelChip label={l} />
              <button type="button" onClick={() => deleteLabel(l)} className="text-pm-faint opacity-0 hover:text-pm-danger group-hover:opacity-100" title="Delete label">
                <Trash2 size={12} />
              </button>
            </span>
          ))}
          <form onSubmit={addLabel} className="inline-flex items-center gap-1">
            <Plus size={13} className="text-pm-faint" />
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="New label"
              className="w-28 rounded-full border border-dashed border-pm-border bg-transparent px-2 py-0.5 text-xs outline-none focus:border-[var(--pm-focus-border)]"
            />
          </form>
        </div>
      </Section>

      <Section title="Custom fields">
        <ul className="mb-2 divide-y divide-pm-border/60">
          {fields.map((f) => (
            <li key={f.id} className="flex items-center gap-2 py-2">
              <span className="text-sm">{f.name}</span>
              <span className="rounded-full bg-pm-raised px-2 py-0.5 text-[10px] uppercase text-pm-faint">{f.type}</span>
              {f.type === 'select' && (
                <input
                  defaultValue={(f.options || []).join(', ')}
                  onBlur={(e) =>
                    tasksAPI
                      .updateCustomField(f.id, {
                        name: f.name,
                        type: f.type,
                        options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      })
                      .then(loadLists)
                  }
                  placeholder="option1, option2"
                  className="min-w-0 flex-1 rounded-md border border-pm-border bg-pm-input px-2 py-1 text-xs outline-none focus:border-[var(--pm-focus-border)]"
                />
              )}
              <button
                type="button"
                onClick={() => tasksAPI.deleteCustomField(f.id).then(loadLists)}
                className="ml-auto rounded p-1 text-pm-faint hover:text-pm-danger"
                title="Delete field (values removed from tasks)"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
          {fields.length === 0 && <li className="py-2 text-xs text-pm-faint">No custom fields yet.</li>}
        </ul>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!newField.trim()) return;
            await tasksAPI.createCustomField(project.id, { name: newField.trim(), type: newFieldType });
            setNewField('');
            await loadLists();
          }}
          className="flex items-center gap-2"
        >
          <input value={newField} onChange={(e) => setNewField(e.target.value)} placeholder="Field name" className={`${inputCls} py-1.5 text-xs`} />
          <select value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)} className={`${inputCls} w-auto py-1.5 text-xs`}>
            {['text', 'number', 'select', 'date', 'currency'].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <button type="submit" disabled={!newField.trim()} className={`${btnGhost} inline-flex items-center gap-1 py-1.5 text-xs`}>
            <Plus size={12} /> Add
          </button>
        </form>
      </Section>

      <Section title="Template & import">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={async () => {
              const tname = window.prompt('Template name:', `${project.name} template`);
              if (!tname) return;
              await templatesAPI.saveAs(project.id, tname);
              window.alert('Template saved — available in the New Project dialog.');
            }}
            className={btnGhost}
          >
            Save as template
          </button>
          <label className={`${btnGhost} cursor-pointer`}>
            Import tasks from CSV
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                const text = await file.text();
                // minimal CSV parse (quoted fields supported)
                const rows = [];
                let row = [], cell = '', inQ = false;
                for (let i = 0; i < text.length; i++) {
                  const ch = text[i];
                  if (inQ) {
                    if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
                    else if (ch === '"') inQ = false;
                    else cell += ch;
                  } else if (ch === '"') inQ = true;
                  else if (ch === ',') { row.push(cell); cell = ''; }
                  else if (ch === '\n' || ch === '\r') {
                    if (ch === '\r' && text[i + 1] === '\n') i++;
                    row.push(cell); cell = '';
                    if (row.some((c) => c.trim())) rows.push(row);
                    row = [];
                  } else cell += ch;
                }
                if (cell || row.length) { row.push(cell); if (row.some((c) => c.trim())) rows.push(row); }
                if (rows.length < 2) { window.alert('CSV needs a header row + at least one data row.'); return; }
                const headers = rows[0].map((h) => h.trim().toLowerCase());
                const mapping = {};
                ['title', 'group', 'due_date', 'priority', 'description'].forEach((k) => {
                  const idx = headers.findIndex((h) => h === k || h === k.replace('_', ' '));
                  if (idx >= 0) mapping[k] = idx;
                });
                if (mapping.title === undefined) { window.alert('CSV must have a "title" column.'); return; }
                const { data } = await templatesAPI.importCsv(project.id, { rows: rows.slice(1), mapping });
                window.alert(`Imported ${data.created} task(s). Columns used: ${Object.keys(mapping).join(', ')}.`);
                await loadLists();
              }}
            />
          </label>
          <span className="text-xs text-pm-faint">
            CSV header row: title (required), group, due_date, priority, description.
          </span>
        </div>
      </Section>

      {user?.role === 'admin' && (
        <Section title="Danger zone">
          <div className="flex items-center justify-between">
            <p className="text-xs text-pm-muted">Permanently delete this project with all its tasks, comments and activity.</p>
            <button type="button" onClick={deleteProject} className={`${btnGhost} shrink-0`} style={{ color: 'var(--pm-danger)', borderColor: 'var(--pm-danger)' }}>
              Delete project
            </button>
          </div>
        </Section>
      )}
    </div>
  );
}
